import { DEFAULT_CSS_CLASS } from '@constants';
import {
  InspectorControlsAppearance,
  InspectorControlsGeneral,
  InspectorTabs,
} from '@components/inspector-tabs';
import Visibility from '@components/visibility';
import Label from '@components/block-label';
import ClassDropdown, { getBlockClassNames } from '@components/class-dropdown';
import {
  setUniqueIdAttribute,
  cloneChildBlockByParentClientId,
} from '@helpers/utils.js';
import classnames from 'classnames';
import { Swiper } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import CommonSettings from './partials/settings/settingsCommon';
import TabContentSettings from './partials/settings/settingsTabContent';
import TabNavSettings from './partials/settings/settingsTabNav';
import {
  setSliderSettingAttribute as updateResponsiveAttributes,
  removeSliderSettingAttribute as updateSliderSettingAttribute,
} from '@helpers/slider-utils.js';
import {
  FocusableIcon,
  FocusableIconSwitcher,
} from '@components/focusable-block-icon';
import { v4 as uuidv4 } from 'uuid';
import { title } from './block.json';
import { tabSliderIcon } from './icons';
import './editor.scss';

const {
  i18n: { __ },
  blockEditor: { useBlockProps, useInnerBlocksProps },
  components: { Button },
  blocks: { createBlock },
  element: { useEffect, useState, useRef },
  data: { withSelect },
} = wp;

const DEFAULT_NUMBER_OF_SLIDES = 3;
const generateTabSliderTemplates = (numberOfSlides) => {
  const tabSliderTemplates = [];
  for (let i = 0; i < numberOfSlides; i++) {
    const tabSliderTemplate = [
      'b3rg/tab-slider-tab',
      {},
      [
        [
          'b3rg/tab-slider-tab-nav',
          {},
          [['core/heading', { level: 3, placeholder: `Tab title ${i + 1}` }]],
        ],
        [
          'b3rg/tab-slider-tab-content',
          {},
          [
            [
              'core/paragraph',
              {
                placeholder: `Tab content ${i + 1} ...`,
              },
            ],
          ],
        ],
      ],
    ];
    tabSliderTemplates.push(tabSliderTemplate);
  }
  return tabSliderTemplates;
};

const Edit = ({
  name,
  attributes,
  setAttributes,
  innerBlocks,
  hasSelectedInnerBlock,
  clientId,
  selectedBlockClientId,
  selectedSlideClientId,
}) => {
  const {
    tabNavSliderSettings,
    tabContentSliderSettings,
    blockVisibility,
    blockClassNames,
    label,
    enableProgressLine,
    enableFocusableIcon,
  } = attributes;
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [swiperInst, setSwiperInst] = useState(null);
  const [t, _t] = useState(null);
  const slideNextButton = useRef();
  const slidePrevButton = useRef();
  const { replaceInnerBlocks, selectBlock } =
    wp.data.dispatch('core/block-editor');

  const defaultBlockCssClass = DEFAULT_CSS_CLASS.TAB_SLIDER;
  const classNames = getBlockClassNames(blockClassNames);
  const blockProps = useBlockProps({
    className: classnames({
      [`${defaultBlockCssClass}`]: true,
      [`${classNames}`]: classNames,
      'has-focusable-icon': enableFocusableIcon,
      disable: !blockVisibility,
    }),
  });
  const { children, ...innerBlockProps } = useInnerBlocksProps(blockProps, {
    allowedBlocks: ['b3rg/tab-slider-tab'],
    templateLock: false,
    template: generateTabSliderTemplates(DEFAULT_NUMBER_OF_SLIDES),
    orientation: 'horizontal',
    __experimentalCaptureToolbars: true,
  });

  useEffect(() => {
    setUniqueIdAttribute(name, 'blockId', attributes, setAttributes);
  }, [name, attributes, setAttributes]);

  useEffect(() => {
    setAttributes({ identifier: uuidv4() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // set active slide
    if (hasSelectedInnerBlock && swiperInst) {
      const selectedSlideIndex = innerBlocks.findIndex(
        (ib) => ib.clientId === selectedSlideClientId,
      );
      if (selectedSlideIndex !== -1) {
        setActiveSlideIndex(selectedSlideIndex);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBlockClientId, selectedSlideClientId, hasSelectedInnerBlock]);

  useEffect(() => {
    if (swiperInst && innerBlocks.length !== swiperInst.slides?.length) {
      _t(Math.random());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innerBlocks]);

  useEffect(() => {
    swiperInst?.update();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const onClickNewSlide = () => {
    const newSlideBlock = createBlock('b3rg/tab-slider-tab');
    const updatedSlidesArr = [...innerBlocks, newSlideBlock];
    replaceInnerBlocks(clientId, updatedSlidesArr);
    setActiveSlideIndex(updatedSlidesArr.length);
    _t(Math.random());
    selectBlock(newSlideBlock.clientId);
  };

  // Update Mobile, Tablet and Table Landscape setting(s) through cloned copies
  const setSliderSettingAttribute = (
    attribute,
    layout,
    property,
    childProperty,
    value,
  ) => {
    updateResponsiveAttributes(
      attributes,
      setAttributes,
      attribute,
      layout,
      property,
      childProperty,
      value,
    );
  };

  // Update Mobile, Tablet and Table Landscape setting(s) through cloned copies
  const removeSliderSettingAttribute = (
    attribute,
    layout,
    attributeKey,
    attributeChildKey,
  ) => {
    updateSliderSettingAttribute(
      attributes,
      setAttributes,
      attribute,
      layout,
      attributeKey,
      attributeChildKey,
    );
  };

  const editViewSliderSettings = {
    direction: 'horizontal',
    effect: 'slide',
    initialSlide: activeSlideIndex,
    loop: false,
    centeredSlides: false,
    observeParents: false,
    observer: false,
    slidesPerGroup: 1,
    slidesPerView: 1,
    spaceBetween: 0,
    speed: 600,
    zoom: false,
    allowTouchMove: false, // draggable - do not turn on for edit view
    modules: [Navigation, Pagination],
    navigation: {
      clickable: true,
      nextEl: slideNextButton.current,
      prevEl: slidePrevButton.current,
    },
    onSwiper: setSwiperInst,
    onAfterInit({ activeIndex }) {
      setActiveSlideIndex(activeIndex);
    },
    onTransitionEnd({ activeIndex }) {
      setActiveSlideIndex(activeIndex);
    },
  };

  children.type.displayName = 'SwiperSlide';

  const onClickSlidePrev = () => {
    swiperInst?.slideTo(activeSlideIndex - 1);
  };

  const onClickSlideNext = () => {
    swiperInst?.slideTo(activeSlideIndex + 1);
  };

  return (
    <>
      <InspectorTabs>
        <InspectorControlsGeneral>
          <Label {...{ label, setAttributes }} />
          <Visibility {...{ blockVisibility, setAttributes }} />
          <CommonSettings
            {...{
              tabNavSliderSettings,
              tabContentSliderSettings,
              setSliderSettingAttribute,
              tabContents: innerBlocks,
              removeSliderSettingAttribute,
              enableProgressLine,
              setAttributes,
            }}
          />
          <TabNavSettings
            {...{
              tabNavSliderSettings,
              setSliderSettingAttribute,
              tabNavs: innerBlocks,
              removeSliderSettingAttribute,
            }}
          />
          <TabContentSettings
            {...{
              tabContentSliderSettings,
              setSliderSettingAttribute,
              tabContents: innerBlocks,
              removeSliderSettingAttribute,
            }}
          />
        </InspectorControlsGeneral>
        <InspectorControlsAppearance>
          <ClassDropdown
            {...{
              blockClassNames,
              setAttributes,
              blockClassPrefix: defaultBlockCssClass,
            }}
          />
          <FocusableIconSwitcher
            {...{
              enableFocusableIcon,
              setAttributes,
            }}
          />
        </InspectorControlsAppearance>
      </InspectorTabs>
      <div {...innerBlockProps}>
        {enableFocusableIcon && (
          <FocusableIcon
            {...{ title, attributes, setAttributes, icon: tabSliderIcon }}
          />
        )}
        <Button
          ref={slidePrevButton}
          className="slider-nav nav-prev components-button is-primary"
          onClick={onClickSlidePrev}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            height="24"
            width="24"
          >
            <g
              xmlns="http://www.w3.org/2000/svg"
              transform="matrix(-1 0 0 -1 24 24)"
            >
              <polygon points="7.293 4.707 14.586 12 7.293 19.293 8.707 20.707 17.414 12 8.707 3.293 7.293 4.707" />
            </g>
          </svg>
        </Button>
        <Swiper
          {...{
            key: t,
            ...editViewSliderSettings,
          }}
        >
          {children}
        </Swiper>
        <Button
          ref={slideNextButton}
          className="slider-nav nav-next components-button is-primary"
          onClick={onClickSlideNext}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
          >
            <polygon points="7.293 4.707 14.586 12 7.293 19.293 8.707 20.707 17.414 12 8.707 3.293 7.293 4.707" />
          </svg>
        </Button>
        <div className="custom-appender-btn-group">
          <button
            className="custom-appender-btn clone-last-item"
            onClick={() => cloneChildBlockByParentClientId(clientId)}
          >
            {__('Clone Last Slide', 'b3rg')}
          </button>
          <button
            className="custom-appender-btn add-empty-slide"
            onClick={onClickNewSlide}
          >
            {__('Add Empty Slide', 'b3rg')}
          </button>
        </div>
      </div>
    </>
  );
};

export default withSelect((select, { clientId }) => {
  const {
    getBlocks,
    getSelectedBlockClientId,
    hasSelectedInnerBlock: getHasSelectedInnerBlock,
    getClientIdsWithDescendants,
    getBlockParents,
    getBlocksByClientId,
  } = select('core/block-editor');
  const innerBlocks = getBlocks(clientId);
  const selectedBlockClientId = getSelectedBlockClientId();
  const descendantClientIds = getClientIdsWithDescendants(clientId);
  const hasSelectedInnerBlock = getHasSelectedInnerBlock(clientId);
  let selectedSlideClientId = hasSelectedInnerBlock
    ? selectedBlockClientId
    : undefined;
  if (
    !hasSelectedInnerBlock &&
    selectedBlockClientId &&
    descendantClientIds.indexOf(selectedBlockClientId) !== -1
  ) {
    const blockParentClientIds = getBlockParents(selectedBlockClientId);
    selectedSlideClientId = getBlocksByClientId(blockParentClientIds).find(
      ({ name }) => name === 'b3rg/tab-slider-tab',
    )?.clientId;
  }
  return {
    innerBlocks,
    selectedBlockClientId,
    hasSelectedInnerBlock:
      hasSelectedInnerBlock || typeof selectedSlideClientId !== 'undefined',
    selectedSlideClientId,
  };
})(Edit);
